import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react-v3/polaris/space-between";
import Container from "@amzn/awsui-components-react-v3/polaris/container";
import Alert from "@amzn/awsui-components-react-v3/polaris/alert";
import ColumnLayout from "@amzn/awsui-components-react-v3/polaris/column-layout";
import StatusIndicator from "@amzn/awsui-components-react-v3/polaris/status-indicator";
import { addCommaToPrice, isPartnerAccount } from "../../common/utilityFunctions";
import { useEffect } from "react";
import Popover from "@amzn/awsui-components-react-v3/polaris/popover";
import { Table } from "@amzn/awsui-components-react-v3";
import Link from "@amzn/awsui-components-react-v3/polaris/link";
import { ENABLE_FTV2_SUPPORT, getPartnerPricePerSeat, isAutoRenewalEnabled, PARTNER_TYPE } from "../../common/constants";
import Checkbox from "@amzn/awsui-components-react-v3/polaris/checkbox";
import FormField from "@amzn/awsui-components-react-v3/polaris/form-field";
import { getNodeEnvironment } from "../../common/nodeEnvironment";
/**
 * Formats currency so it will return a decimal only if there is a cent value
 * @param amount dollar value
 * @returns a string with the currency value, will return 0 if input is null
 */
export const formatCurrency = (amount) => {
    const returnAmount = amount ?? 0;
    if (returnAmount % 1 === 0) {
        return addCommaToPrice(returnAmount.toString());
    }
    else {
        return addCommaToPrice(returnAmount.toFixed(2));
    }
};
/**
 * Returns seat range as string in the format `min seats - max seats` for display in discount table
 * @param lowerDiscount
 * @param higherDiscount
 * @returns
 */
export const getPrettySeatRange = (lowerDiscount, higherDiscount) => {
    if (!lowerDiscount)
        return '';
    if (!higherDiscount)
        return `${lowerDiscount.seatThreshold}+`;
    return `${lowerDiscount.seatThreshold} - ${higherDiscount.seatThreshold - 1}`;
};
export const sortDiscountListDesc = (discountList) => {
    return [...discountList].sort((a, b) => b.seatThreshold - a.seatThreshold);
};
export const sortDiscountListAsc = (discountList) => {
    return [...discountList].sort((a, b) => a.seatThreshold - b.seatThreshold);
};
/**
 * Table inside the discount tooltip
 */
export const DiscountTable = ({ discountList }) => {
    const sortedDiscountList = sortDiscountListAsc(discountList);
    const eligibleDiscounts = sortedDiscountList.map((discount, ind) => {
        return {
            id: `discount-${discount.percentageDiscount}`,
            seatRange: getPrettySeatRange(discount, sortedDiscountList[ind + 1]),
            discount: `${discount.percentageDiscount}%`
        };
    });
    return (_jsx(Table, { contentDensity: "compact", variant: "embedded", items: eligibleDiscounts, columnDefinitions: [
            {
                id: 'number_of_seats',
                header: 'Number of seats',
                cell: item => item.seatRange
            },
            {
                id: 'discount',
                header: 'Discount (%)',
                cell: item => item.discount
            }
        ] }));
};
/**
 * Tooltip to display eligible discounts
 * @param param0
 * @returns
 */
export const DiscountPopover = ({ discountList, t }) => {
    if (!discountList || !discountList.length) {
        return _jsx(_Fragment, {});
    }
    return (_jsx(Popover, { dismissButton: false, position: "right", size: "large", triggerType: "text", content: _jsx(DiscountTable, { discountList: discountList }), children: _jsx(Link, { variant: "info", children: t('subscription_form_eligible_discounts_text') }) }));
};
/**
 * Returns discount % based on the number of seats entered
 * @param numberOfSeats
 * @param discountList
 * @returns
 */
export const getApplicableDiscountPercentage = (numberOfSeats, discountList) => {
    const sortedList = [...discountList].sort((a, b) => b.seatThreshold - a.seatThreshold);
    const applicableDiscount = sortedList.find((discount) => numberOfSeats >= discount.seatThreshold);
    return applicableDiscount ? applicableDiscount.percentageDiscount : 0;
};
/**
 * Returns discount amount based on the number of seats entered
 * @param numberOfSeats
 * @param pricePerSeat
 * @param discountList
 * @returns
 */
export const getDiscountAmount = (numberOfSeats, pricePerSeat, discountList) => {
    const applicableDiscount = getApplicableDiscountPercentage(numberOfSeats, discountList);
    return (numberOfSeats * pricePerSeat * applicableDiscount) / 100;
};
export const getDiscountList = (offerPriceData) => {
    return offerPriceData?.getPayerAccountOfferPrice?.discountList?.map(a => {
        if (a) {
            return { seatThreshold: a?.seatThreshold, percentageDiscount: a?.percentageDiscount };
        }
    }) || [];
};
export const getEndCustomerCountryValue = (awsAccountType, formData) => isPartnerAccount(awsAccountType) && formData?.endCustomerCountry?.value ? formData.endCustomerCountry.value : '';
export const SubscriptionSummaryContainer = (props) => {
    const { t } = useTranslation();
    const pricePerSeatMap = props?.offerPriceData?.getPayerAccountOfferPrice?.pricePerSeatMap;
    const endCustomerCountry = getEndCustomerCountryValue(props.awsAccountType, props.formData);
    const pricePerSeat = isPartnerAccount(props.awsAccountType)
        ? getPartnerPricePerSeat(pricePerSeatMap, endCustomerCountry)
        : props?.offerPriceData?.getPayerAccountOfferPrice?.pricePerSeat;
    const discountList = getDiscountList(props?.offerPriceData);
    const PRICE_THRESHOLD = {
        ATP: 359.2,
        DISTI: 314.3
    };
    const tax = 0;
    const payerAccount = props.offerPriceData?.getPayerAccountOfferPrice?.payerAccountId;
    const numberOfSeats = props.numberOfSeats && !Number.isNaN(props.numberOfSeats) ? +props.numberOfSeats : 0;
    const annualTotal = numberOfSeats * pricePerSeat;
    const discount = getDiscountAmount(numberOfSeats, pricePerSeat, discountList);
    const discountPercentage = getApplicableDiscountPercentage(numberOfSeats, discountList);
    const orderTotal = annualTotal + tax - discount;
    const customerType = props?.offerPriceData?.getPayerAccountOfferPrice?.requesterType;
    const isEligibleForPromoPricing = isPartnerAccount(props.awsAccountType) &&
        ((customerType === PARTNER_TYPE.ATP && pricePerSeat < PRICE_THRESHOLD.ATP) ||
            (customerType === PARTNER_TYPE.DISTI && pricePerSeat < PRICE_THRESHOLD.DISTI));
    useEffect(() => {
        props.setDiscount(discountPercentage);
    }, [discountPercentage]);
    return (_jsx(Container, { header: _jsxs(_Fragment, { children: [_jsx(Box, { variant: "h2", children: t("subscription_form_summary_header") }), _jsx(DiscountPopover, { discountList: discountList, t: t })] }), children: _jsxs(SpaceBetween, { size: "m", children: [_jsxs(SpaceBetween, { size: "xxxs", children: [_jsxs(ColumnLayout, { columns: 2, children: [_jsxs(Box, { children: [t("subscription_form_price_per_seat_label"), ":"] }), _jsx(Box, { float: "right", children: pricePerSeat ? `${formatCurrency(pricePerSeat)}` : '-' })] }), isEligibleForPromoPricing && (_jsx(ColumnLayout, { columns: 1, children: _jsx(Box, { children: _jsx(StatusIndicator, { children: t("Promotional_Pricing_label") }) }) })), _jsxs(ColumnLayout, { columns: 2, children: [_jsxs(Box, { children: [t("subscription_form_number_of_seats_label"), ":"] }), _jsx(Box, { float: "right", children: props.numberOfSeats && !Number.isNaN(props.numberOfSeats) ? numberOfSeats : '-' })] }), _jsxs(ColumnLayout, { columns: 2, children: [_jsxs(Box, { children: [t("subscription_form_annual_total_label"), ":"] }), _jsx(Box, { float: "right", children: annualTotal ? `${formatCurrency(annualTotal)}` : '-' })] }), discount
                            ? _jsxs(ColumnLayout, { columns: 2, children: [_jsxs(Box, { children: [t("subscription_form_discount_label"), ":"] }), _jsx(Box, { float: "right", children: discount ? `${formatCurrency(discount)}` : '-' })] })
                            : _jsx(_Fragment, {})] }), _jsxs(ColumnLayout, { columns: 2, children: [_jsxs(Box, { variant: "h3", children: [t("subscription_form_order_total_label"), ":"] }), _jsx(Box, { variant: "h3", float: "right", children: orderTotal ? `${formatCurrency(orderTotal)}` : '-' })] }), ENABLE_FTV2_SUPPORT[getNodeEnvironment()] && props.isFTv2Account && (_jsx(Alert, { "data-testid": "configure_subscription_ftv2_account_alert", type: "info", header: t("subscription_form_ftv2_account_warning_header"), children: _jsxs(Box, { variant: "div", color: "text-body-secondary", children: [_jsx(Box, { children: t("subscription_form_ftv2_account_warning") }), _jsx(Link, { href: "https://aws.amazon.com/free/terms/", external: true, children: t("subscription_form_ftv2_learn_more_text") })] }) })), _jsx(Alert, { statusIconAriaLabel: "Info", children: t("subscription_form_summary_info", { awsAccountId: payerAccount }) }), _jsx(Box, { variant: "small", children: isPartnerAccount(props.awsAccountType)
                        ? _jsxs(_Fragment, { children: [t("subscription_form_terms_and_conditions_partner_customer_text_start"), _jsx(Link, { href: "https://aws.amazon.com/training/learn2earn", external: true, variant: "primary", fontSize: "body-s", children: t("subscription_form_terms_and_conditions_learn2earn_link") }), t("subscription_form_terms_and_conditions_agreement_text"), _jsx(Link, { href: "https://www.aws.amazon.com/agreement", external: true, variant: "primary", fontSize: "body-s", children: t("subscription_form_terms_and_conditions_agreement_link") }), t("subscription_form_terms_and_conditions_text_end")] })
                        : _jsxs(_Fragment, { children: [t("subscription_form_terms_and_conditions_regular_customer_text_start"), _jsx(Link, { href: "https://www.aws.amazon.com/agreement", external: true, variant: "primary", fontSize: "body-s", children: t("subscription_form_terms_and_conditions_agreement_link") }), t("subscription_form_terms_and_conditions_text_end")] }) }), (!isPartnerAccount(props.awsAccountType) && isAutoRenewalEnabled() && props.formData.autoRenewal) && (_jsx(Box, { children: _jsx(FormField, { errorText: !props.formData.agreementChecked ? t("subscription_form_agreement_required_error") : "", children: _jsx(Checkbox, { "data-testid": "agreement-checkbox", checked: props.formData.agreementChecked, onChange: props.onFormDataChange.agreementChecked, children: t("subscription_form_agree_to_recurring_checkbox_label") }) }) }))] }) }));
};
